import { PLACES } from '@/data/places'
import { RARITY_COLORS } from '@/data/constants'
import type { HuntResult } from '@/utils/hunting'
import styles from './ResultModal.module.css'

interface PendingResult {
  type: 'travel' | 'hunt'
  place?: string
  huntResult?: HuntResult
}

interface ResultModalProps {
  result: PendingResult
  onClose: () => void
}

export function ResultModal({ result, onClose }: ResultModalProps) {
  if (result.type === 'travel') {
    return <TravelResult place={result.place!} onClose={onClose} />
  }

  return <HuntResultModal result={result.huntResult!} onClose={onClose} />
}

interface TravelResultProps {
  place: string
  onClose: () => void
}

function TravelResult({ place, onClose }: TravelResultProps) {
  const placeData = PLACES[place]

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.icon}>🗺️</div>
        <h3 className={styles.title}>Arrived!</h3>
        <p className={styles.subtitle}>
          You've reached <strong>{placeData?.name}</strong>
        </p>
        <button className={styles.continueBtn} onClick={onClose}>
          Continue
        </button>
      </div>
    </div>
  )
}

interface HuntResultModalProps {
  result: HuntResult
  onClose: () => void
}

function HuntResultModal({ result, onClose }: HuntResultModalProps) {
  if (!result.success) {
    return (
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.icon}>🌫️</div>
          <h3 className={styles.title}>Nothing Found</h3>
          <p className={styles.flavorText}>
            {getFailureText()}
          </p>
          <button className={styles.continueBtn} onClick={onClose}>
            Continue
          </button>
        </div>
      </div>
    )
  }

  const echo = result.echo!
  const isGlimmering = result.isGlimmering

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={`${styles.modal} ${styles.successModal}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          '--rarity-color': RARITY_COLORS[echo.rarity],
        } as React.CSSProperties}
      >
        <div className={styles.discoveryGlow} />

        {isGlimmering && (
          <div className={styles.glimmeringBanner}>
            ✨ Glimmering! ✨
          </div>
        )}

        <div className={`${styles.echoImage} ${isGlimmering ? styles.glimmering : ''}`}>
          <img src={echo.image} alt={echo.name} />
        </div>

        <h3 className={styles.echoName}>{echo.name}</h3>

        <span
          className={styles.rarityBadge}
          style={{ color: RARITY_COLORS[echo.rarity] }}
        >
          {echo.rarity.toUpperCase()}
        </span>

        <p className={styles.echoConcept}>{echo.concept}</p>

        <button className={styles.continueBtn} onClick={onClose}>
          Add to Codex
        </button>
      </div>
    </div>
  )
}

function getFailureText(): string {
  const texts = [
    "The rustling faded before you could get close...",
    "Fresh tracks in the mud, but the creature is gone.",
    "A faint glow disappeared into the mist.",
    "You caught a glimpse of something, but it slipped away.",
    "The air feels thick with presence, yet nothing reveals itself.",
    "Whispers in the wind, but no Echo to be found.",
  ]
  return texts[Math.floor(Math.random() * texts.length)]!
}
